/* eslint-disable i18next/no-literal-string */
import {api} from '@/api/client'
import {endpoints} from '@/api/endpoints'
import type {Page} from '@/types/page'
import type {Party, PartyListItem, PartyCreateRequest, PartyUpdateRequest} from '@/types/party'
import type {Identity, IdentityCreateRequest, IdentityListItem} from '@/types/identity'
import type {Account} from '@/types/account'
import type {UserWithRoles} from '@/types/role-assignments'
import type {EntityLink} from '@/types/entity-link'
import type {AccessProfileAssignmentListItem} from '@/types/assignment'
import type {Role, RoleCreateRequest, RoleUpdateRequest, AuthPermission} from '@/types/roles'
import type {TargetSystem} from '@/types/system'
import type {Resource, ResourceListItem, ResourceOwner, ResourceAncestor} from '@/types/resource'
import type {EnforcementObject} from '@/types/enforcement-object'
import type {AccessProfile, InheritedAccessProfile} from '@/types/access-profile'
import type {ApprovalPolicy} from '@/types/approval-policy'
import type {RestrictionRule, RestrictionRuleProfile, RestrictionRuleTestResult} from '@/types/restriction-rule'
import type {AccessRequest, AccessRequestBulkCreateRequest, AccessRequestCreateRequest, AccessRequestItem, BulkAccessRequest} from '@/types/access-request'
import type {AttributeDefinition} from '@/types/attribute-definition'
import type {AttributeEntityType} from '@/types/attribute-entity-type'
import type {Notification, AdminNotification} from '@/types/notification'
import type {JobDefinition, JobExecution, JobExecutionListItem, JobStats, TaskDefinition, ErrorLog, ErrorLogStats, ClusterNode} from '@/types/background-jobs'
import type {SqlPreviewRequest} from '@/types/sql-preview'
import type {
    CampaignSummaryByPolicy,
    CertificationCampaign,
    CertificationPolicy,
    CertificationReviewItem,
    CertificationDecision,
    CertificationDashboardSummary,
    ActiveCampaignDetail,
    CreateCampaignRequest,
    UpdateCampaignRequest,
    CreateCertificationPolicyRequest,
    UpdateCertificationPolicyRequest,
    ReviewItemNavigation,
    TestExpressionRequest,
    TestExpression,
    PreviewScopeRequest,
    PreviewScopeResponse,
} from '@/types/certification'
// CPD-OFF — connector type imports mirror services/types.ts by design (service interface + implementation)
import type {OutboundConnector, OutboundConnectorList, OutboundConnectorLog, OutboundConnectorCreateRequest, OutboundConnectorUpdateRequest, OutboundConnectorTestResult} from '@/types/outbound-connector'
import type {InboundConnector, InboundConnectorList, InboundConnectorCreateRequest, InboundConnectorUpdateRequest, ConnectionTest, InboundSyncRun, InboundSyncRecord, SyncTriggerResponse, PreviewResponse, SyncStatus, RestInboundConfig, RestInboundConfigUpdateRequest, ScimInboundConfig, ScimInboundConfigUpdateRequest, InboundConnectorSettingsUpdateRequest, InboundAttributeMappingResponse, ScriptInboundConfig, ScriptInboundConfigCreateRequest, ScriptInboundConfigUpdateRequest, InboundScriptValidationResult} from '../types'
// CPD-ON
import type {AutoAssignRule, AutoAssignRuleRequest, AutoAssignRuleExecutionLog, AutoAssignRuleVersion, AutoAssignRuleProfile} from '@/types/auto-assign-rule'
import type {RuleTest, ValidateExpression} from '@/types/auto-assign-rule-request'
import type {Constraint, ConstraintSummary, CreateConstraintRequest, UpdateConstraintRequest} from '@/types/constraint'
import type {Violation, ViolationSummary, AckViolationRequest, BulkAckRequest, BulkAction, EntityViolationSummary} from '@/types/violation'
import type {FunctionGroupAssignment, FunctionGroupRequest, FunctionGroupRequestItem, FunctionGroupAccessProfileList, InheritedAccessProfileAssignment, AccessProfileFunctionGroup} from '@/types/function-group'
import type {FunctionGroup} from '@/types/function-group'
import type {Delegation, DelegationCreateRequest} from '@/types/delegation'
import type {FormLayout, FormLayoutListItem, Palette} from '@/types/form-layout'
import type {ValueList} from '@/types/value-list'
import type {TypeTableResponse, LocalesResponse, TypeTableSummary, PromotedTypeView} from '@/types/types'
import type {Health, TraceListItem, SessionList} from '@/types/observability'
import type {Onboarding, OnboardingRequest} from '@/types/onboarding'
import type {EffectiveAccess, DashboardStats} from '@/types/effective-access'
import type {Services, QueryParams} from '../types'
import type * as ST from '../types'
import type {RehireReviewCandidate, RehireReviewDecideRequest, RehireReviewDecideResponse, RehireReviewCountResponse, RehireReviewStatus, PreviousAccessProfile} from '@/types/rehire-review'

function buildQueryString(params?: QueryParams): string {
    if (!params) return ''
    const entries = Object.entries(params).filter(([, v]) => v !== undefined)
    if (entries.length === 0) return ''
    return '?' + entries.flatMap(([k, v]) =>
        Array.isArray(v) ? v.map(item => `${k}=${encodeURIComponent(String(item))}`) : [`${k}=${encodeURIComponent(String(v))}`]
    ).join('&')
}

function createRealPartiesService(): ST.PartiesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<PartyListItem>>(`${endpoints.parties.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<Party>(endpoints.parties.detail(id)),
        create: (req: PartyCreateRequest) =>
            api.post<Party>(endpoints.parties.list(), req),
        update: (id: string, req: PartyUpdateRequest) =>
            api.put<Party>(endpoints.parties.detail(id), req),
        attributes: (id: string) =>
            api.get(endpoints.parties.attributes(id)),
        ancestors: (id: string) =>
            api.get<{id: string; derivedDisplayName: string; partyType: string}[]>(`${endpoints.parties.detail(id)}/ancestors`),
        sources: (id: string) =>
            api.get<import('@/types/source').SourceListResponse>(`${endpoints.parties.detail(id)}/sources`),
    }
}

function createRealIdentitiesService(): ST.IdentitiesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<IdentityListItem>>(`${endpoints.identities.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<Identity>(endpoints.identities.detail(id)),
        create: (req: IdentityCreateRequest) =>
            api.post<Identity>(endpoints.identities.list(), req),
        update: (id: string, req: Partial<Identity>) =>
            api.put<Identity>(endpoints.identities.detail(id), req),
        byParty: (partyId: string) =>
            api.get<IdentityListItem[]>(endpoints.identities.byParty(partyId)),
        attributes: (id: string) =>
            api.get<unknown[]>(endpoints.identities.attributes(id)),
        identifiers: (id: string) =>
            api.get<import('@/types/identity').Identifier[]>(endpoints.identities.identifiers(id)),
        assignments: (id: string) =>
            api.get<AccessProfileAssignmentListItem[]>(endpoints.identities.assignments(id)),
        accounts: (id: string) =>
            api.get<Account[]>(endpoints.identities.accounts(id)),
        timeline: (id: string, params?: QueryParams) =>
            api.get(`${endpoints.identities.timeline(id)}${buildQueryString(params)}`),
        grants: (id: string, params?: QueryParams) =>
            api.get<import('@/types/effective-access').EffectiveAccess[]>(`${endpoints.identities.grants(id)}${buildQueryString(params)}`),
        grantStats: (id: string) =>
            api.get<import('@/types/effective-access').GrantStats>(endpoints.identities.grantStats(id)),
        updateLifecycle: (id: string, req: import('@/types/identity').IdentityLifecycleRequest) =>
            api.put<Identity>(endpoints.identities.updateLifecycle(id), req),
        updateReactivationDate: (id: string, req: import('@/types/identity').UpdateReactivationDateRequest) =>
            api.put<Identity>(endpoints.identities.updateReactivationDate(id), req),
        rehire: (id: string, req: import('@/types/identity').RehireRequest) =>
            api.post<Identity>(endpoints.identities.rehire(id), req),
        revokeAssignment: (id: string, assignmentId: string) =>
            api.post(endpoints.identities.revokeAssignment(id, assignmentId), {}),
        evaluateRules: (id: string) =>
            api.post(endpoints.identities.evaluateRules(id), {}),
        recalculateGrants: (id: string) =>
            api.post(endpoints.identities.recalculateGrants(id), {}),
        compareGrants: (idA: string, idB: string) =>
            api.get<{common: import('@/types/effective-access').EffectiveAccess[]; onlyInA: import('@/types/effective-access').EffectiveAccess[]; onlyInB: import('@/types/effective-access').EffectiveAccess[]; commonCount: number; onlyInACount: number; onlyInBCount: number}>(endpoints.identities.compareGrants(idA, idB)),
        accessSnapshot: (id: string, params?: QueryParams) =>
            api.get(`${endpoints.identities.accessSnapshot(id)}${buildQueryString(params)}`),
        identitySnapshot: (id: string, params?: QueryParams) =>
            api.get(`${endpoints.identities.identitySnapshot(id)}${buildQueryString(params)}`),
        inheritedAccessProfileAssignments: (id: string) =>
            api.get<InheritedAccessProfileAssignment[]>(endpoints.identities.inheritedAccessProfileAssignments(id)),
        sources: (id: string) =>
            api.get<import('@/types/source').SourceListResponse>(`${endpoints.identities.detail(id)}/sources`),
        lifecycleEvents: (id: string, params?: QueryParams) =>
            api.get<Page<import('@/types/identity').IdentityLifecycleEvent>>(
                `${endpoints.identities.lifecycleEvents(id)}${buildQueryString(params)}`),
    }
}

function createRealRolesService(): ST.RolesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<Role>>(`${endpoints.roles.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<Role>(endpoints.roles.detail(id)),
        create: (req: RoleCreateRequest) =>
            api.post<Role>(endpoints.roles.create(), req),
        update: (id: string, req: RoleUpdateRequest) =>
            api.put<Role>(endpoints.roles.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.roles.remove(id)),
        permissions: () =>
            api.get(endpoints.roles.permissions()),
        updatePermissions: (id: string, req: unknown) =>
            api.put(endpoints.roles.updatePermissions(id), req),
    }
}

function createRealPermissionsService(): ST.PermissionsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<AuthPermission>>(
                `${endpoints.permissions.list()}${buildQueryString(params)}`,
            ),
        detail: (id: string) =>
            api.get<AuthPermission>(endpoints.permissions.detail(id)),
    }
}

function createRealSystemsService(): ST.SystemsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<TargetSystem>>(`${endpoints.systems.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<TargetSystem>(endpoints.systems.detail(id)),
        create: (req: import('@/types/system').TargetSystemCreateRequest) =>
            api.post<TargetSystem>(endpoints.systems.list(), req),
        update: (id: string, req: Partial<TargetSystem>) =>
            api.put<TargetSystem>(endpoints.systems.detail(id), req),
        remove: (id: string) =>
            api.delete<void>(endpoints.systems.detail(id)),
        grants: (id: string, params?: QueryParams) =>
            api.get<Page<import('@/types/effective-access').EffectiveAccess>>(`${endpoints.systems.grants(id)}${buildQueryString(params)}`),
        owners: (id: string) =>
            api.get<import('@/types/system').TargetSystemOwner[]>(endpoints.systems.owners(id)),
        addOwner: (id: string, req: import('@/types/owner').OwnerRequest) =>
            api.post<import('@/types/system').TargetSystemOwner>(endpoints.systems.owners(id), req),
        removeOwner: (id: string, ownerId: string) =>
            api.delete<void>(endpoints.systems.removeOwner(id, ownerId)),
    }
}

function createRealResourcesService(): ST.ResourcesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<ResourceListItem>>(`${endpoints.resources.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<Resource>(endpoints.resources.detail(id)),
        create: (req: import('@/types/resource').ResourceCreateRequest) =>
            api.post<Resource>(endpoints.resources.list(), req),
        update: (id: string, req: Partial<Resource>) =>
            api.put<Resource>(endpoints.resources.detail(id), req),
        remove: (id: string) =>
            api.delete<void>(endpoints.resources.detail(id)),
        owners: (id: string) =>
            api.get<ResourceOwner[]>(endpoints.resources.owners(id)),
        addOwner: (id: string, req: import('@/types/owner').OwnerRequest) =>
            api.post<ResourceOwner>(endpoints.resources.owners(id), req),
        removeOwner: (id: string, ownerId: string) =>
            api.delete<void>(endpoints.resources.removeOwner(id, ownerId)),
        bySystem: (systemId: string) =>
            api.get<Page<ResourceListItem>>(endpoints.resources.bySystem(systemId)),
        children: (id: string) =>
            api.get<ResourceListItem[]>(endpoints.resources.children(id)),
        ancestors: (id: string) =>
            api.get<ResourceAncestor[]>(endpoints.resources.ancestors(id)),
    }
}

function createRealEnforcementObjectsService(): ST.EnforcementObjectsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<EnforcementObject>>(`${endpoints.enforcementObjects.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<EnforcementObject>(endpoints.enforcementObjects.detail(id)),
        create: (req: Partial<EnforcementObject>) =>
            api.post<EnforcementObject>(endpoints.enforcementObjects.list(), req),
        update: (id: string, req: Partial<EnforcementObject>) =>
            api.put<EnforcementObject>(endpoints.enforcementObjects.detail(id), req),
        byResource: (resourceId: string) =>
            api.get<Page<EnforcementObject>>(endpoints.enforcementObjects.byResource(resourceId))
                .then(page => page.content ?? []),
        bySystem: (systemId: string) =>
            api.get<Page<EnforcementObject>>(endpoints.enforcementObjects.bySystem(systemId))
                .then(page => page.content ?? []),
    }
}

function createRealAccessProfilesService(): ST.AccessProfilesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<import('@/types/access-profile').AccessProfileListItem>>(`${endpoints.accessProfiles.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<AccessProfile>(endpoints.accessProfiles.detail(id)),
        create: (req: import('@/types/access-profile').AccessProfileCreateRequest) =>
            api.post<AccessProfile>(endpoints.accessProfiles.create(), req),
        update: (id: string, req: import('@/types/access-profile').AccessProfileUpdateRequest) =>
            api.put<AccessProfile>(endpoints.accessProfiles.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.accessProfiles.remove(id)),
        activate: (id: string) =>
            api.post<AccessProfile>(endpoints.accessProfiles.activate(id), {}),
        deprecate: (id: string) =>
            api.post<AccessProfile>(endpoints.accessProfiles.deprecate(id), {}),
        owners: (id: string) =>
            api.get<import('@/types/access-profile').AccessProfileOwner[]>(endpoints.accessProfiles.owners(id)),
        addOwner: (id: string, req: import('@/types/owner').OwnerRequest) =>
            api.post<import('@/types/access-profile').AccessProfileOwner>(endpoints.accessProfiles.owners(id), req),
        removeOwner: (id: string, ownerId: string) =>
            api.delete(endpoints.accessProfiles.removeOwner(id, ownerId)),
        bindings: (id: string, params?: QueryParams) =>
            api.get<import('@/types/access-profile').AccessProfileBinding[]>(`${endpoints.accessProfiles.bindings(id)}${buildQueryString(params)}`),
        addBinding: (id: string, req: import('@/types/access-profile').AccessProfileBindingCreateRequest) =>
            api.post<import('@/types/access-profile').AccessProfileBinding>(endpoints.accessProfiles.addBinding(id), req),
        removeBinding: (bindingId: string) =>
            api.delete(endpoints.accessProfiles.removeBinding(bindingId)),
        byResourceInherited: (resourceId: string) =>
            api.get<InheritedAccessProfile[]>(endpoints.accessProfiles.byResourceInherited(resourceId)),
        byEnforcementObject: (eoId: string) =>
            api.get<import('@/types/access-profile').AccessProfileListItem[]>(endpoints.accessProfiles.byEnforcementObject(eoId)),
        functionGroups: (id: string) =>
            api.get<AccessProfileFunctionGroup[]>(endpoints.accessProfiles.functionGroups(id)),
    }
}

function createRealServiceAccountsService(): ST.ServiceAccountsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<import('@/types/service-account').ServiceAccount>>(
                `${endpoints.serviceAccounts.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<import('@/types/service-account').ServiceAccount>(endpoints.serviceAccounts.detail(id)),
        create: (req: import('@/types/service-account').ServiceAccountCreateRequest) =>
            api.post<import('@/types/service-account').ServiceAccount>(endpoints.serviceAccounts.create(), req),
        update: (id: string, req: import('@/types/service-account').ServiceAccountUpdateRequest) =>
            api.put<import('@/types/service-account').ServiceAccount>(endpoints.serviceAccounts.update(id), req),
        deactivate: (id: string) =>
            api.delete<import('@/types/service-account').ServiceAccount>(endpoints.serviceAccounts.remove(id)),
    }
}

function createRealApiKeysService(): ST.ApiKeysService {
    return {
        list: (serviceAccountId: string) =>
            api.get<import('@/types/service-account').ApiKey[]>(endpoints.apiKeys.list(serviceAccountId)),
        mint: (serviceAccountId: string, req: import('@/types/service-account').ApiKeyCreateRequest) =>
            api.post<import('@/types/service-account').ApiKeyMint>(endpoints.apiKeys.mint(serviceAccountId), req),
        revoke: (serviceAccountId: string, keyId: string) =>
            api.post<import('@/types/service-account').ApiKey>(endpoints.apiKeys.revoke(serviceAccountId, keyId), {}),
    }
}

function createRealServiceAccountRolesService(): ST.ServiceAccountRolesService {
    return {
        list: (serviceAccountId: string) =>
            api.get<import('@/types/roles').RoleAssignment[]>(endpoints.serviceAccountRoles.list(serviceAccountId)),
        assign: (serviceAccountId: string, req: import('@/types/roles').AssignRoleRequest) =>
            api.post<import('@/types/roles').RoleAssignment>(endpoints.serviceAccountRoles.assign(serviceAccountId), req),
        remove: (serviceAccountId: string, roleId: string) =>
            api.delete<void>(endpoints.serviceAccountRoles.remove(serviceAccountId, roleId)),
    }
}

function createRealApprovalPoliciesService(): ST.ApprovalPoliciesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<ApprovalPolicy>>(`${endpoints.approvalPolicies.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<ApprovalPolicy>(endpoints.approvalPolicies.detail(id)),
        create: (req: import('@/types/approval-policy').CreateApprovalPolicyRequest) =>
            api.post<ApprovalPolicy>(endpoints.approvalPolicies.create(), req),
        update: (id: string, req: import('@/types/approval-policy').UpdateApprovalPolicyRequest) =>
            api.put<ApprovalPolicy>(endpoints.approvalPolicies.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.approvalPolicies.remove(id)),
    }
}

function createRealRestrictionRulesService(): ST.RestrictionRulesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<RestrictionRule>>(`${endpoints.restrictionRules.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<RestrictionRule>(endpoints.restrictionRules.detail(id)),
        create: (req: Partial<RestrictionRule>) =>
            api.post<RestrictionRule>(endpoints.restrictionRules.create(), req),
        update: (id: string, req: Partial<RestrictionRule>) =>
            api.put<RestrictionRule>(endpoints.restrictionRules.update(id), req),
        activate: (id: string) =>
            api.post<RestrictionRule>(endpoints.restrictionRules.activate(id), {}),
        deactivate: (id: string) =>
            api.post<RestrictionRule>(endpoints.restrictionRules.deactivate(id), {}),
        profiles: (id: string) =>
            api.get<RestrictionRuleProfile[]>(endpoints.restrictionRules.profiles(id)),
        violations: (params?: QueryParams) =>
            api.get(`${endpoints.restrictionRules.violations()}${buildQueryString(params)}`),
        ruleViolations: (id: string, params?: QueryParams) =>
            api.get(`${endpoints.restrictionRules.ruleViolations(id)}${buildQueryString(params)}`),
        detect: (id: string) =>
            api.post(endpoints.restrictionRules.detect(id), {}),
        allDetections: (params?: QueryParams) =>
            api.get(`${endpoints.restrictionRules.allDetections()}${buildQueryString(params)}`),
        detections: (id: string, params?: QueryParams) =>
            api.get(`${endpoints.restrictionRules.detections(id)}${buildQueryString(params)}`),
        versions: (id: string, params?: QueryParams) =>
            api.get(`${endpoints.restrictionRules.versions(id)}${buildQueryString(params)}`),
        test: (req: unknown) =>
            api.post(endpoints.restrictionRules.test(), req),
        testSavedRule: (id: string) =>
            api.post(endpoints.restrictionRules.testSavedRule(id), {}),
        owners: (id: string) =>
            api.get<import('@/types/owner').OwnerResponse[]>(endpoints.restrictionRules.owners(id)),
        addOwner: (id: string, req: import('@/types/owner').OwnerRequest) =>
            api.post<import('@/types/owner').OwnerResponse>(endpoints.restrictionRules.owners(id), req),
        removeOwner: (id: string, ownerId: string) =>
            api.delete<void>(endpoints.restrictionRules.removeOwner(id, ownerId)),
    }
}

function createRealRestrictionRuleRequestsService(): ST.RestrictionRuleRequestsService {
    return {
        list: (params?: QueryParams) =>
            api.get(`${endpoints.restrictionRuleRequests.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get(endpoints.restrictionRuleRequests.detail(id)),
        create: (req: unknown) =>
            api.post(endpoints.restrictionRuleRequests.create(), req),
        update: (id: string, req: unknown) =>
            api.put(endpoints.restrictionRuleRequests.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.restrictionRuleRequests.remove(id)),
        submit: (id: string) =>
            api.post(endpoints.restrictionRuleRequests.submit(id), {}),
        approve: (id: string, req: unknown) =>
            api.post(endpoints.restrictionRuleRequests.approve(id), req),
        reject: (id: string, req: unknown) =>
            api.post(endpoints.restrictionRuleRequests.reject(id), req),
        cancel: (id: string) =>
            api.post(endpoints.restrictionRuleRequests.cancel(id), {}),
        myApprovals: (params?: QueryParams) =>
            api.get(`${endpoints.restrictionRuleRequests.myApprovals()}${buildQueryString(params)}`),
        myApprovalsCount: () =>
            api.get(endpoints.restrictionRuleRequests.myApprovalsCount()),
        test: (id: string) =>
            api.post<RestrictionRuleTestResult>(endpoints.restrictionRuleRequests.test(id), {}),
        validateExpression: (req) =>
            api.post(endpoints.restrictionRuleRequests.validateExpression(), req),
    }
}

function createRealAccessRequestsService(): ST.AccessRequestsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<AccessRequest>>(`${endpoints.accessRequests.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<AccessRequest>(endpoints.accessRequests.detail(id)),
        create: (req: AccessRequestCreateRequest) =>
            api.post<AccessRequest>(endpoints.accessRequests.create(), req),
        createBulk: (req: AccessRequestBulkCreateRequest) =>
            api.post<BulkAccessRequest>(endpoints.accessRequests.createBulk(), req),
        update: (id: string, req: Partial<AccessRequest>) =>
            api.put<AccessRequest>(endpoints.accessRequests.detail(id), req),
        myRequests: (params?: QueryParams) =>
            api.get<Page<AccessRequest>>(`${endpoints.accessRequests.myRequests()}${buildQueryString(params)}`),
        myApprovals: (params?: QueryParams) =>
            api.get<Page<AccessRequestItem>>(`${endpoints.accessRequests.myApprovals()}${buildQueryString(params)}`),
        myApprovalsCount: () =>
            api.get<number>(endpoints.accessRequests.myApprovalsCount()),
        cancel: (id: string) =>
            api.post(endpoints.accessRequests.cancel(id), {}),
        approveItem: (itemId: string, req: unknown) =>
            api.post(endpoints.accessRequests.approveItem(itemId), req),
        rejectItem: (itemId: string, req: unknown) =>
            api.post(endpoints.accessRequests.rejectItem(itemId), req),
        cancelItem: (itemId: string) =>
            api.post(endpoints.accessRequests.cancelItem(itemId), {}),
        batchApprove: (req: unknown) =>
            api.post(endpoints.accessRequests.batchApprove(), req),
        batchReject: (req: unknown) =>
            api.post(endpoints.accessRequests.batchReject(), req),
        peerRecommendations: (beneficiaryId: string) =>
            api.get<import('@/types/access-request').PeerAccessProfile[]>(endpoints.accessRequests.peerRecommendations(beneficiaryId)),
        profileHoldings: (beneficiaryIdentityId: string, profileIds: string[]) =>
            api.get<Record<string, import('@/types/holding-status').HoldingStatusResponse>>(
                `${endpoints.accessRequests.profileHoldings()}?beneficiaryIdentityId=${beneficiaryIdentityId}&profileIds=${profileIds.join(',')}`),
    }
}

function createRealCertificationService(): ST.CertificationService {
    return {
        listCampaigns: (params?: QueryParams) =>
            api.get<Page<CertificationCampaign>>(`${endpoints.certificationCampaigns.list()}${buildQueryString(params)}`),
        detailCampaign: (id: string) =>
            api.get<CertificationCampaign>(endpoints.certificationCampaigns.detail(id)),
        createCampaign: (req: CreateCampaignRequest) =>
            api.post<CertificationCampaign>(endpoints.certificationCampaigns.create(), req),
        updateCampaign: (id: string, req: UpdateCampaignRequest) =>
            api.put<CertificationCampaign>(endpoints.certificationCampaigns.update(id), req),
        removeCampaign: (id: string) =>
            api.delete(endpoints.certificationCampaigns.remove(id)),
        activateCampaign: (id: string) =>
            api.post<CertificationCampaign>(endpoints.certificationCampaigns.activate(id), {}),
        cancelCampaign: (id: string) =>
            api.post<CertificationCampaign>(endpoints.certificationCampaigns.cancel(id), {}),
        listPolicies: (params?: QueryParams) =>
            api.get<Page<CertificationPolicy>>(`${endpoints.certificationPolicies.list()}${buildQueryString(params)}`),
        detailPolicy: (id: string) =>
            api.get<CertificationPolicy>(endpoints.certificationPolicies.detail(id)),
        createPolicy: (req: CreateCertificationPolicyRequest) =>
            api.post<CertificationPolicy>(endpoints.certificationPolicies.create(), req),
        updatePolicy: (id: string, req: UpdateCertificationPolicyRequest) =>
            api.put<CertificationPolicy>(endpoints.certificationPolicies.update(id), req),
        removePolicy: (id: string) =>
            api.delete(endpoints.certificationPolicies.remove(id)),
        listReviewItems: (campaignId: string, params?: QueryParams) =>
            api.get<Page<CertificationReviewItem>>(`${endpoints.certificationCampaigns.reviewItems(campaignId)}${buildQueryString(params)}`),
        listMyReviews: (params?: QueryParams) =>
            api.get<Page<CertificationReviewItem>>(`${endpoints.certificationReviews.myReviews()}${buildQueryString(params)}`),
        listDecisions: (reviewItemId: string, params?: QueryParams) =>
            api.get<Page<CertificationDecision>>(`${endpoints.certificationReviews.decisions(reviewItemId)}${buildQueryString(params)}`),
        myCampaigns: (params?: QueryParams) =>
            api.get<CertificationCampaign[]>(`${endpoints.certificationReviews.myCampaigns()}${buildQueryString(params)}`),
        detailReview: (reviewItemId: string) =>
            api.get<CertificationReviewItem>(endpoints.certificationReviews.detail(reviewItemId)),
        generateCampaign: (req: {ownerPartyId: string}) =>
            api.post<import('@/types/certification').GenerateCampaigns>(
                `${endpoints.certificationCampaigns.generate()}${buildQueryString({ownerPartyId: req.ownerPartyId})}`,
                {}),
        campaignStatistics: (id: string) =>
            api.get<import('@/types/certification').CampaignStatistics>(endpoints.certificationCampaigns.statistics(id)),
        campaignPreviewStatistics: (id: string) =>
            api.get<import('@/types/certification').PreviewStatistics>(endpoints.certificationCampaigns.previewStatistics(id)),
        decide: (reviewItemId: string, req: unknown) =>
            api.post(endpoints.certificationReviews.decide(reviewItemId), req),
        bulkDecide: (reviewItemId: string, req: unknown) =>
            api.post(endpoints.certificationReviews.bulkDecide(reviewItemId), req),
        certify: (reviewItemId: string, req: unknown) =>
            api.post(endpoints.certificationReviews.certify(reviewItemId), req),
        bulkCertify: (req: unknown) =>
            api.post(endpoints.certificationReviews.bulkCertify(), req),
        reviewNavigation: (reviewItemId: string, params?: QueryParams) =>
            api.get<ReviewItemNavigation>(`${endpoints.certificationReviews.navigation(reviewItemId)}${buildQueryString(params)}`),
        reassign: (reviewItemId: string, req: unknown) =>
            api.post(endpoints.certificationReviews.reassign(reviewItemId), req),
        dashboardSummary: () =>
            api.get<CertificationDashboardSummary>(endpoints.certificationDashboard.summary()),
        activeCampaigns: () =>
            api.get<ActiveCampaignDetail[]>(endpoints.certificationDashboard.activeCampaigns()),
    }
}

function createRealAttributeDefinitionsService(): ST.AttributeDefinitionsService {
    return {
        list: (entityType, params) =>
            api.get<AttributeDefinition[]>(`${endpoints.attributeDefinitions.list(entityType)}${buildQueryString(params ?? {})}`),
        detail: (entityType, id) =>
            api.get<AttributeDefinition>(endpoints.attributeDefinitions.detail(entityType, id)),
        create: (entityType, req) =>
            api.post<AttributeDefinition>(endpoints.attributeDefinitions.create(entityType), req),
        update: (entityType, id, req) =>
            api.put<AttributeDefinition>(endpoints.attributeDefinitions.update(entityType, id), req),
        remove: (entityType, id, body) =>
            api.delete(endpoints.attributeDefinitions.remove(entityType, id), body),
        allowedValueSources: () =>
            api.get<string[]>(endpoints.attributeDefinitions.allowedValueSources()),
        reorder: (entityType, req) =>
            api.put(endpoints.attributeDefinitions.reorder(entityType), req),
        resolveValuesDraft: (req) =>
            api.post(endpoints.attributeDefinitions.resolveValuesDraft(), req),
        translations: (entityType, id) =>
            api.get(endpoints.attributeDefinitions.translations(entityType, id)),
        updateTranslations: (entityType, id, req) =>
            api.put(endpoints.attributeDefinitions.translations(entityType, id), req),
    }
}

function createRealValueListsService(): ST.ValueListsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<ValueList>>(`${endpoints.valueLists.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<ValueList>(endpoints.valueLists.detail(id)),
        create: (req: Partial<ValueList>) =>
            api.post<ValueList>(endpoints.valueLists.create(), req),
        update: (id: string, req: Partial<ValueList>) =>
            api.put<ValueList>(endpoints.valueLists.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.valueLists.remove(id)),
        addEntry: (id: string, req: unknown) =>
            api.post(endpoints.valueLists.addEntry(id), req),
        updateEntry: (id: string, entryId: string, req: unknown) =>
            api.put(endpoints.valueLists.updateEntry(id, entryId), req),
        removeEntry: (id: string, entryId: string) =>
            api.delete(endpoints.valueLists.removeEntry(id, entryId)),
        reorderEntries: (id: string, req: unknown) =>
            api.put(endpoints.valueLists.reorderEntries(id), req),
    }
}

function createRealTypesService(): ST.TypesService {
    return {
        list: (params?: QueryParams) =>
            api.get<TypeTableSummary[]>(`${endpoints.types.list()}${buildQueryString(params)}`),
        locales: () =>
            api.get<LocalesResponse>(endpoints.types.locales()),
        promoted: () =>
            api.get<PromotedTypeView[]>(endpoints.types.promoted()),
        detail: (tableName: string) =>
            api.get<TypeTableResponse>(endpoints.types.detail(tableName)),
        create: (tableName: string, req: unknown) =>
            api.post(endpoints.types.create(tableName), req),
        remove: (tableName: string, code: string) =>
            api.delete(endpoints.types.remove(tableName, code)),
        toggleActive: (tableName: string, code: string, active: boolean) =>
            api.put(endpoints.types.toggleActive(tableName, code), {active}),
        updateTranslations: (tableName: string, code: string, req: unknown) =>
            api.put(endpoints.types.updateTranslations(tableName, code), req),
        updateSettings: (tableName: string, code: string, req: unknown) =>
            api.put(endpoints.types.updateSettings(tableName, code), req),
    }
}

function createRealNotificationsService(): ST.NotificationsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<Notification>>(`${endpoints.notifications.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<Notification>(endpoints.notifications.detail(id)),
        unreadCount: () =>
            api.get<{count: number}>(endpoints.notifications.unreadCount()),
        markRead: (id: string) =>
            api.put(endpoints.notifications.markRead(id), {}),
        markAllRead: () =>
            api.put(endpoints.notifications.markAllRead(), {}),
        adminLog: (params?: QueryParams) =>
            api.get<Page<AdminNotification>>(`${endpoints.notifications.log()}${buildQueryString(params)}`),
        badgeCount: () =>
            api.get<{patternCount: number}>(endpoints.errorLogs.badgeCount()),
    }
}

function createRealJobsService(): ST.JobsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<JobDefinition>>(`${endpoints.jobs.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<JobDefinition>(endpoints.jobs.detail(id)),
        create: (req: unknown) =>
            api.post<JobDefinition>(endpoints.jobs.create(), req),
        update: (id: string, req: unknown) =>
            api.put<JobDefinition>(endpoints.jobs.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.jobs.remove(id)),
        stats: () =>
            api.get<JobStats>(endpoints.jobs.stats()),
        byKey: (jobKey: string) =>
            api.get<JobDefinition>(endpoints.jobs.byKey(jobKey)),
        trigger: (jobId: string) =>
            api.post(endpoints.jobs.trigger(jobId), {}),
        schedule: (jobId: string, req: unknown) =>
            api.put(endpoints.jobs.schedule(jobId), req),
        tasks: (jobId: string) =>
            api.get<TaskDefinition[]>(endpoints.jobs.tasks(jobId)),
        updateTasks: (jobId: string, req: unknown) =>
            api.put(endpoints.jobs.updateTasks(jobId), req),
        taskPalette: (jobId: string) =>
            api.get(endpoints.jobs.taskPalette(jobId)),
        builder: (jobId: string) =>
            api.get(endpoints.jobs.builder(jobId)),
        preview: (jobId: string) =>
            api.get(endpoints.jobs.preview(jobId)),
        jobExecutions: (jobId: string, params?: QueryParams) =>
            api.get<Page<JobExecutionListItem>>(`${endpoints.jobs.jobExecutions(jobId)}${buildQueryString(params)}`),
        validateScript: (req: unknown) =>
            api.post(endpoints.jobs.validateScript(), req),
        scriptAssist: (req: unknown) =>
            api.post(endpoints.jobs.scriptAssist(), req),
        updateTaskLogLevel: (taskId: string, req: unknown) =>
            api.put(endpoints.jobs.updateTaskLogLevel(taskId), req),
        executions: (params?: QueryParams) =>
            api.get<Page<JobExecutionListItem>>(`${endpoints.executions.list()}${buildQueryString(params)}`),
        executionDetail: (id: string) =>
            api.get<JobExecutionListItem>(endpoints.executions.detail(id)),
        executionTasks: (id: string) =>
            api.get<import('@/types/background-jobs').TaskExecutionTreeItem[]>(endpoints.executions.tasks(id)),
        cancelExecution: (execId: string) =>
            api.post(endpoints.executions.cancel(execId), {}),
        testScript: (taskId: string, req: unknown) =>
            api.post(`${endpoints.jobs.detail(taskId)}/test-script`, req),
    }
}

function createRealErrorLogsService(): ST.ErrorLogsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<ErrorLog>>(`${endpoints.errorLogs.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<ErrorLog>(endpoints.errorLogs.detail(id)),
        stats: () =>
            api.get<ErrorLogStats>(endpoints.errorLogs.stats()),
        errorTypes: (params?: QueryParams) =>
            api.get<Page<ErrorLog>>(`${endpoints.errorLogs.errorTypes()}${buildQueryString(params)}`),
        errorTypeOccurrences: (templateId: string, params?: QueryParams) =>
            api.get<Page<ErrorLog>>(`${endpoints.errorLogs.errorTypeOccurrences(templateId)}${buildQueryString(params)}`),
        badgeCount: () =>
            api.get<{patternCount: number}>(endpoints.errorLogs.badgeCount()),
        contextKeys: () =>
            api.get<string[]>(endpoints.errorLogs.contextKeys()),
        deleteAll: () =>
            api.delete(endpoints.errorLogs.deleteAll()),
        generateTestData: () =>
            api.post(endpoints.errorLogs.generateTestData(), {}),
        bySource: (params?: QueryParams) =>
            api.get(`${endpoints.errorLogs.bySource()}${buildQueryString(params)}`),
    }
}

function createRealObservabilityService(): ST.ObservabilityService {
    return {
        health: () =>
            api.get<Health>(endpoints.observability.health()),
        traces: (params?: QueryParams) =>
            api.get<Page<TraceListItem>>(`${endpoints.observability.traces()}${buildQueryString(params)}`),
        trace: (sessionId: string) =>
            api.get<import('@/types/observability').Trace>(endpoints.observability.trace(sessionId)),
        sessions: (params?: QueryParams) =>
            api.get<Page<SessionList>>(`${endpoints.observability.sessions()}${buildQueryString(params)}`),
    }
}

function createRealInboundConnectorsService(): ST.InboundConnectorsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<InboundConnector>>(`${endpoints.inboundConnectors.list()}${buildQueryString(params)}`),
        listItems: (params?: QueryParams) =>
            api.get<Page<InboundConnectorList>>(`${endpoints.inboundConnectors.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<InboundConnector>(endpoints.inboundConnectors.detail(id)),
        create: (req: InboundConnectorCreateRequest) =>
            api.post<InboundConnector>(endpoints.inboundConnectors.create(), req),
        update: (id: string, req: InboundConnectorUpdateRequest) =>
            api.put<InboundConnector>(endpoints.inboundConnectors.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.inboundConnectors.remove(id)),
        test: (id: string) =>
            api.post<ConnectionTest>(endpoints.inboundConnectors.test(id), {}),
        syncRuns: (id: string, params?: QueryParams) =>
            api.get<Page<InboundSyncRun>>(`${endpoints.inboundConnectors.syncRuns(id)}${buildQueryString(params)}`),
        syncRunDetail: (id: string, runId: string) =>
            api.get<InboundSyncRun>(endpoints.inboundConnectors.syncRunDetail(id, runId)),
        syncRecords: (id: string, runId: string, params?: QueryParams) =>
            api.get<Page<InboundSyncRecord>>(`${endpoints.inboundConnectors.syncRecords(id, runId)}${buildQueryString(params)}`),
        approveSyncRun: (id: string, runId: string) =>
            api.post<InboundSyncRun>(endpoints.inboundConnectors.approveSyncRun(id, runId), {}),
        rejectSyncRun: (id: string, runId: string) =>
            api.post<InboundSyncRun>(endpoints.inboundConnectors.rejectSyncRun(id, runId), {}),
        syncStatus: (id: string) =>
            api.get<SyncStatus>(endpoints.inboundConnectors.syncStatus(id)),
        enable: (id: string) =>
            api.post<InboundConnector>(endpoints.inboundConnectors.enable(id), {}),
        disable: (id: string) =>
            api.post<InboundConnector>(endpoints.inboundConnectors.disable(id), {}),
        syncFull: (id: string) =>
            api.post<SyncTriggerResponse>(endpoints.inboundConnectors.syncFull(id), {}),
        syncDelta: (id: string) =>
            api.post<SyncTriggerResponse>(endpoints.inboundConnectors.syncDelta(id), {}),
        syncPreview: (id: string) =>
            api.get<PreviewResponse>(endpoints.inboundConnectors.syncPreview(id)),
        restConfig: (id: string) =>
            api.get<RestInboundConfig>(endpoints.inboundConnectors.restConfig(id)),
        updateRestConfig: (id: string, req: RestInboundConfigUpdateRequest) =>
            api.put<RestInboundConfig>(endpoints.inboundConnectors.updateRestConfig(id), req),
        scimConfig: (id: string) =>
            api.get<ScimInboundConfig>(endpoints.inboundConnectors.scimConfig(id)),
        updateScimConfig: (id: string, req: ScimInboundConfigUpdateRequest) =>
            api.put<ScimInboundConfig>(endpoints.inboundConnectors.updateScimConfig(id), req),
        updateSettings: (id: string, req: InboundConnectorSettingsUpdateRequest) =>
            api.put<InboundConnector>(endpoints.inboundConnectors.updateSettings(id), req),
        attributeMappings: (id: string) =>
            api.get<InboundAttributeMappingResponse>(endpoints.inboundConnectors.attributeMappings(id)),
        updateAttributeMappings: (id: string, req: unknown) =>
            api.put<InboundAttributeMappingResponse>(endpoints.inboundConnectors.attributeMappings(id), req),
        scriptConfig: (id: string) =>
            api.get<ScriptInboundConfig>(endpoints.inboundConnectors.inboundScriptConfig(id)),
        createScriptConfig: (id: string, req: ScriptInboundConfigCreateRequest) =>
            api.post<ScriptInboundConfig>(endpoints.inboundConnectors.inboundScriptConfig(id), req),
        updateScriptConfig: (id: string, req: ScriptInboundConfigUpdateRequest) =>
            api.put<ScriptInboundConfig>(endpoints.inboundConnectors.inboundScriptConfig(id), req),
        validateInboundScript: (id: string, req: {script: string}) =>
            api.post<InboundScriptValidationResult>(endpoints.inboundConnectors.inboundScriptConfigValidate(id), req),
        testInboundScriptConnection: (id: string) =>
            api.post<{success: boolean; message: string | null; responseTimeMs: number | null}>(endpoints.inboundConnectors.inboundScriptConfigTestConnection(id), {}),
        sapHcmConfig: (id: string) =>
            api.get<import('@/types/inbound-connector').SapHcmInboundConfig>(endpoints.inboundConnectors.sapHcmConfig(id)),
        createSapHcmConfig: (id: string, req: unknown) =>
            api.post<import('@/types/inbound-connector').SapHcmInboundConfig>(endpoints.inboundConnectors.sapHcmConfig(id), req),
        updateSapHcmConfig: (id: string, req: unknown) =>
            api.put<import('@/types/inbound-connector').SapHcmInboundConfig>(endpoints.inboundConnectors.sapHcmConfig(id), req),
        sapSfEcConfig: (id: string) =>
            api.get<import('@/types/inbound-connector').SapSfEcInboundConfig>(endpoints.inboundConnectors.sapSfEcConfig(id)),
        createSapSfEcConfig: (id: string, req: unknown) =>
            api.post<import('@/types/inbound-connector').SapSfEcInboundConfig>(endpoints.inboundConnectors.sapSfEcConfig(id), req),
        updateSapSfEcConfig: (id: string, req: unknown) =>
            api.put<import('@/types/inbound-connector').SapSfEcInboundConfig>(endpoints.inboundConnectors.sapSfEcConfig(id), req),
    }
}

function createRealOutboundConnectorsService(): ST.OutboundConnectorsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<OutboundConnectorList>>(`${endpoints.outboundConnectors.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<OutboundConnector>(endpoints.outboundConnectors.detail(id)),
        create: (req: OutboundConnectorCreateRequest) =>
            api.post<OutboundConnector>(endpoints.outboundConnectors.create(), req),
        update: (id: string, req: OutboundConnectorUpdateRequest) =>
            api.put<OutboundConnector>(endpoints.outboundConnectors.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.outboundConnectors.remove(id)),
        test: (id: string) =>
            api.post<OutboundConnectorTestResult>(endpoints.outboundConnectors.test(id), {}),
        logs: (id: string, params?: QueryParams) =>
            api.get<Page<OutboundConnectorLog>>(`${endpoints.outboundConnectors.logs(id)}${buildQueryString(params)}`),
        bySystem: (systemId: string) =>
            api.get<OutboundConnector | null>(endpoints.outboundConnectors.bySystem(systemId)),
        createScimConfig: (id: string, req: unknown) =>
            api.post(endpoints.outboundConnectors.scimConfig(id), req),
        createLdapConfig: (id: string, req: unknown) =>
            api.post(endpoints.outboundConnectors.ldapConfig(id), req),
        createRestConfig: (id: string, req: unknown) =>
            api.post(endpoints.outboundConnectors.restConfig(id), req),
        createDatabaseConfig: (id: string, req: unknown) =>
            api.post(endpoints.outboundConnectors.databaseConfig(id), req),
        createK8sConfig: (id: string, req: unknown) =>
            api.post(endpoints.outboundConnectors.k8sConfig(id), req),
        createEmailConfig: (id: string, req: unknown) =>
            api.post(endpoints.outboundConnectors.emailConfig(id), req),
        updateScimConfig: (id: string, req: unknown) =>
            api.put(endpoints.outboundConnectors.scimConfig(id), req),
        updateLdapConfig: (id: string, req: unknown) =>
            api.put(endpoints.outboundConnectors.ldapConfig(id), req),
        updateRestConfig: (id: string, req: unknown) =>
            api.put(endpoints.outboundConnectors.restConfig(id), req),
        updateDatabaseConfig: (id: string, req: unknown) =>
            api.put(endpoints.outboundConnectors.databaseConfig(id), req),
        updateK8sConfig: (id: string, req: unknown) =>
            api.put(endpoints.outboundConnectors.k8sConfig(id), req),
        updateEmailConfig: (id: string, req: unknown) =>
            api.put(endpoints.outboundConnectors.emailConfig(id), req),
        updateAttributeMappings: (id: string, req: unknown) =>
            api.put(endpoints.outboundConnectors.updateAttributeMappings(id), req),
        getScriptConfig: (id: string) =>
            api.get(endpoints.outboundConnectors.scriptConfig(id)),
        createScriptConfig: (id: string, req: unknown) =>
            api.post(endpoints.outboundConnectors.scriptConfig(id), req),
        updateScriptConfig: (id: string, req: unknown) =>
            api.put(endpoints.outboundConnectors.scriptConfig(id), req),
        validateScript: (id: string, req: unknown) =>
            api.post(endpoints.outboundConnectors.scriptConfigValidate(id), req),
        sapAbapConfig: (id: string) =>
            api.get<import('@/types/outbound-connector').SapAbapOutboundConfig>(endpoints.outboundConnectors.sapAbapConfig(id)),
        createSapAbapConfig: (id: string, req: unknown) =>
            api.post<import('@/types/outbound-connector').SapAbapOutboundConfig>(endpoints.outboundConnectors.sapAbapConfig(id), req),
        updateSapAbapConfig: (id: string, req: unknown) =>
            api.put<import('@/types/outbound-connector').SapAbapOutboundConfig>(endpoints.outboundConnectors.sapAbapConfig(id), req),
        provisioningTasks: (id: string, params?: QueryParams) =>
            api.get<Page<import('@/types/provisioning').ProvisioningTask>>(`${endpoints.outboundConnectors.provisioningTasks(id)}${buildQueryString(params)}`),
        provisioningHistory: (id: string, params?: QueryParams) =>
            api.get<Page<import('@/types/provisioning').ProvisioningTaskHistory>>(`${endpoints.outboundConnectors.provisioningHistory(id)}${buildQueryString(params)}`),
    }
}

function createRealReportingService(): ST.ReportingService {
    return {
        categories: (params?: QueryParams) =>
            api.get<Page<import('@/types/background-jobs').ReportCategoryListItem>>(`${endpoints.reporting.categories()}${buildQueryString(params)}`),
        categoryDetail: (id: string) =>
            api.get<import('@/types/background-jobs').ReportCategoryDetail>(endpoints.reporting.categoryDetail(id)),
        createCategory: (req: unknown) =>
            api.post<import('@/types/background-jobs').ReportCategoryDetail>(endpoints.reporting.categories(), req),
        updateCategory: (id: string, req: unknown) =>
            api.put<import('@/types/background-jobs').ReportCategoryDetail>(endpoints.reporting.categoryDetail(id), req),
        removeCategory: (id: string) =>
            api.delete(endpoints.reporting.categoryDetail(id)),
        transferCategory: (id: string, req: unknown) =>
            api.put<import('@/types/background-jobs').ReportCategoryDetail>(endpoints.reporting.transferCategory(id), req),
        moveCategory: (id: string, req: unknown) =>
            api.put<import('@/types/background-jobs').ReportCategoryDetail>(endpoints.reporting.moveCategory(id), req),
        categoryReports: (categoryId: string, params?: QueryParams) =>
            api.get<import('@/types/background-jobs').ReportDefinitionListItem[]>(`${endpoints.reporting.categoryReports(categoryId)}${buildQueryString(params)}`),
        createReport: (categoryId: string, req: unknown) =>
            api.post<import('@/types/background-jobs').ReportDefinitionDetail>(endpoints.reporting.categoryReports(categoryId), req),
        reportDetail: (reportId: string) =>
            api.get<import('@/types/background-jobs').ReportDefinitionDetail>(endpoints.reporting.reportDetail(reportId)),
        updateReport: (reportId: string, req: unknown) =>
            api.put<import('@/types/background-jobs').ReportDefinitionDetail>(endpoints.reporting.reportDetail(reportId), req),
        moveReport: (reportId: string, req: unknown) =>
            api.post<import('@/types/background-jobs').ReportDefinitionDetail>(endpoints.reporting.moveReport(reportId), req),
        deleteReport: (reportId: string) =>
            api.delete(endpoints.reporting.deleteReport(reportId)),
        searchReports: (params?: QueryParams) =>
            api.get<import('@/types/page').Page<import('@/pages/reporting/types').ReportSearchResult>>(`${endpoints.reporting.searchReports()}${buildQueryString(params)}`),
        bulkDeleteReports: (req: unknown) =>
            api.post(endpoints.reporting.bulkDeleteReports(), req),
        bulkMoveReports: (req: unknown) =>
            api.post(endpoints.reporting.bulkMoveReports(), req),
        executeReport: (reportId: string, req: unknown) =>
            api.post<import('@/pages/reporting/types').ReportExecution>(endpoints.reporting.executeReport(reportId), req),
        reportExecutions: (reportId: string, params?: QueryParams) =>
            api.get(`${endpoints.reporting.reportExecutions(reportId)}${buildQueryString(params)}`),
        executionDetail: (executionId: string) =>
            api.get(endpoints.reporting.executionDetail(executionId)),
        exportReport: (reportId: string) =>
            api.get(endpoints.reporting.exportReport(reportId)),
        reorderCategories: (req: unknown) =>
            api.put(endpoints.reporting.reorderCategories(), req),
        reorderReports: (req: unknown) =>
            api.put(endpoints.reporting.reorderReports(), req),
        sqlAssist: (req: unknown) =>
            api.post(endpoints.reporting.sqlAssist(), req),
        sqlExecute: (req: unknown) =>
            api.post(endpoints.reporting.sqlExecute(), req),
        sqlModels: () =>
            api.get(endpoints.reporting.sqlModels()),
        sqlSchema: () =>
            api.get(endpoints.reporting.sqlSchema()),
        reportAttributeDefinitions: (reportId: string) =>
            api.get(endpoints.reporting.reportAttributeDefinitions(reportId)),
        createReportAttributeDefinition: (reportId: string, req: unknown) =>
            api.post(endpoints.reporting.reportAttributeDefinitions(reportId), req),
        updateReportAttributeDefinitions: (reportId: string, req: unknown) =>
            api.put(endpoints.reporting.reportAttributeDefinitions(reportId), req),
        removeReportAttributeDefinition: (reportId: string, defId: string) =>
            api.delete(endpoints.reporting.reportAttributeDefinition(reportId, defId)),
    }
}

function createRealScriptsService(): ST.ScriptsService {
    return {
        // NOTE: callers should use the useSqlPreview hook — handles error shaping, wire-body filtering, retry policy. Direct calls bypass all of that.
        sqlPreview: (req: SqlPreviewRequest) =>
            api.post(endpoints.scripts.sqlPreview(), req),
    }
}

function createRealFormLayoutsService(): ST.FormLayoutsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<FormLayoutListItem>>(`${endpoints.formLayouts.list()}${buildQueryString(params)}`),
        detail: (useCaseKey: string) =>
            api.get<FormLayout>(endpoints.formLayouts.detail(useCaseKey)),
        upsert: (useCaseKey: string, req: unknown) =>
            api.put<FormLayout>(endpoints.formLayouts.upsert(useCaseKey), req),
        remove: (useCaseKey: string) =>
            api.delete(endpoints.formLayouts.delete(useCaseKey)),
        palette: (useCaseKey: string) =>
            api.get<Palette>(endpoints.formLayouts.palette(useCaseKey)),
        getFull: (useCaseKey: string) =>
            api.get<FormLayout>(endpoints.formLayouts.full(useCaseKey)),
    }
}

function createRealConstraintsService(): ST.ConstraintsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<Constraint>>(`${endpoints.constraints.list()}${buildQueryString(params)}`),
        listSummary: (params?: QueryParams) =>
            api.get<Page<ConstraintSummary>>(`${endpoints.constraints.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<Constraint>(endpoints.constraints.detail(id)),
        create: (req: CreateConstraintRequest) =>
            api.post<Constraint>(endpoints.constraints.list(), req),
        update: (id: string, req: UpdateConstraintRequest) =>
            api.put<Constraint>(endpoints.constraints.detail(id), req),
        remove: (id: string) =>
            api.delete(endpoints.constraints.detail(id)),
        enable: (id: string) =>
            api.post<Constraint>(endpoints.constraints.enable(id), {}),
        disable: (id: string) =>
            api.post<Constraint>(endpoints.constraints.disable(id), {}),
        review: (id: string) =>
            api.post<Constraint>(endpoints.constraints.review(id), {}),
        trigger: (id: string) =>
            api.post<Constraint>(endpoints.constraints.trigger(id), {}),
    }
}

function createRealViolationsService(): ST.ViolationsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<ViolationSummary>>(`${endpoints.violations.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<Violation>(endpoints.violations.detail(id)),
        summaryByEntity: (entityTable: string, entityId: string) =>
            api.get<EntityViolationSummary>(
                `${endpoints.violations.summaryByEntity()}${buildQueryString({entityTable, entityId})}`),
        ack: (id: string, req: AckViolationRequest) =>
            api.post<Violation>(endpoints.violations.ack(id), req),
        bulkAck: (req: BulkAckRequest) =>
            api.post<BulkAction>(endpoints.violations.bulkAck(), req),
    }
}

function createRealAutoAssignRulesService(): ST.AutoAssignRulesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<AutoAssignRule>>(`${endpoints.autoAssignRules.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<AutoAssignRule>(endpoints.autoAssignRules.detail(id)),
        create: (req: Partial<AutoAssignRule>) =>
            api.post<AutoAssignRule>(endpoints.autoAssignRules.list(), req),
        update: (id: string, req: Partial<AutoAssignRule>) =>
            api.put<AutoAssignRule>(endpoints.autoAssignRules.detail(id), req),
        listRequests: (params?: QueryParams) =>
            api.get<Page<AutoAssignRuleRequest>>(`${endpoints.autoAssignRules.requests()}${buildQueryString(params)}`),
        detailRequest: (id: string) =>
            api.get<AutoAssignRuleRequest>(endpoints.autoAssignRules.requestDetail(id)),
        executions: (ruleId: string, params?: QueryParams) =>
            api.get<Page<AutoAssignRuleExecutionLog>>(`${endpoints.autoAssignRules.executions(ruleId)}${buildQueryString(params)}`),
        allExecutions: (params?: QueryParams) =>
            api.get<Page<AutoAssignRuleExecutionLog>>(`${endpoints.autoAssignRules.allExecutions()}${buildQueryString(params)}`),
        createRequest: (req: unknown) =>
            api.post<AutoAssignRuleRequest>(endpoints.autoAssignRules.createRequest(), req),
        updateRequest: (id: string, req: unknown) =>
            api.put<AutoAssignRuleRequest>(endpoints.autoAssignRules.updateRequest(id), req),
        removeRequest: (id: string) =>
            api.delete(endpoints.autoAssignRules.removeRequest(id)),
        submitRequest: (id: string) =>
            api.post<AutoAssignRuleRequest>(endpoints.autoAssignRules.submitRequest(id), {}),
        approveRequest: (id: string, req: unknown) =>
            api.post<AutoAssignRuleRequest>(endpoints.autoAssignRules.approveRequest(id), req),
        rejectRequest: (id: string, req: unknown) =>
            api.post<AutoAssignRuleRequest>(endpoints.autoAssignRules.rejectRequest(id), req),
        myApprovals: (params?: QueryParams) =>
            api.get<Page<AutoAssignRuleRequest>>(`${endpoints.autoAssignRules.myApprovals()}${buildQueryString(params)}`),
        myApprovalsCount: () =>
            api.get<number>(endpoints.autoAssignRules.myApprovalsCount()),
        versions: (ruleId: string) =>
            api.get<AutoAssignRuleVersion[]>(endpoints.autoAssignRules.versions(ruleId)),
        profiles: (ruleId: string) =>
            api.get<AutoAssignRuleProfile[]>(endpoints.autoAssignRules.profiles(ruleId)),
        test: (ruleId: string, req: unknown) =>
            api.post<RuleTest>(endpoints.autoAssignRules.test(ruleId), req),
        validateExpression: (req: unknown) =>
            api.post<ValidateExpression>(endpoints.autoAssignRules.validateExpression(), req),
        testExpression: (req: unknown) =>
            api.post<RuleTest>(endpoints.autoAssignRules.testExpression(), req),
        testExistingRequest: (requestId: string, req: unknown) =>
            api.post<RuleTest>(endpoints.autoAssignRules.testExistingRequest(requestId), req),
        evaluate: (id: string) =>
            api.post<void>(endpoints.autoAssignRules.evaluate(id), {}),
        runImpactAnalysis: (id: string, req: ST.ImpactAnalysisRequest) =>
            api.post<ST.ImpactAnalysisResponse>(endpoints.autoAssignRules.impactAnalysis(id), req),
        triggerFullEvaluation: async () => {
            const job = await api.get<JobDefinition>(endpoints.jobs.byKey('start-of-day'))
            await api.post<unknown>(endpoints.jobs.trigger(job.id), {})
        },
        owners: (id: string) =>
            api.get<import('@/types/owner').OwnerResponse[]>(endpoints.autoAssignRules.owners(id)),
        addOwner: (id: string, req: import('@/types/owner').OwnerRequest) =>
            api.post<import('@/types/owner').OwnerResponse>(endpoints.autoAssignRules.owners(id), req),
        removeOwner: (id: string, ownerId: string) =>
            api.delete<void>(endpoints.autoAssignRules.removeOwner(id, ownerId)),
    }
}

function createRealDelegationsService(): ST.DelegationsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<Delegation>>(`${endpoints.delegations.adminList()}${buildQueryString(params)}`),
        adminList: (params?: QueryParams) =>
            api.get<Page<Delegation>>(`${endpoints.delegations.adminList()}${buildQueryString(params)}`),
        adminRevoke: (id: string) =>
            api.post(endpoints.delegations.adminRevoke(id), {}),
        create: (req: DelegationCreateRequest) =>
            api.post<Delegation>(endpoints.delegations.myCreate(), req),
        revoke: (id: string) =>
            api.post(endpoints.delegations.adminRevoke(id), {}),
        myIncoming: (params?: QueryParams) =>
            api.get<Delegation[]>(`${endpoints.delegations.myIncoming()}${buildQueryString(params)}`),
        myOutgoing: (params?: QueryParams) =>
            api.get<Delegation[]>(`${endpoints.delegations.myOutgoing()}${buildQueryString(params)}`),
        mySearch: (params?: QueryParams) =>
            api.get<Page<Delegation>>(`${endpoints.delegations.mySearch()}${buildQueryString(params)}`),
        myCreate: (req: DelegationCreateRequest) =>
            api.post<Delegation>(endpoints.delegations.myCreate(), req),
        myUpdate: (id: string, req: unknown) =>
            api.put<Delegation>(endpoints.delegations.myUpdate(id), req),
        myRevoke: (id: string) =>
            api.post(endpoints.delegations.myRevoke(id), {}),
        forIdentity: (identityId: string) =>
            api.get<Delegation[]>(endpoints.delegations.forIdentity(identityId)),
    }
}

function createRealAnalyticsService(): ST.AnalyticsService {
    return {
        effectiveAccess: (params?: QueryParams) =>
            api.get<Page<EffectiveAccess>>(`${endpoints.effectiveAccess.stats()}${buildQueryString(params)}`),
        dashboardStats: (params?: QueryParams) =>
            api.get<DashboardStats>(`${endpoints.effectiveAccess.stats()}${buildQueryString(params)}`),
        calculationStatus: (identityId: string) =>
            api.get<import('@/types/effective-access').CalculationStatus>(endpoints.effectiveAccess.calculationStatus(identityId)),
        lifecycleEvents: (params?: QueryParams) =>
            api.get<Page<import('@/types/lifecycle-event').LifecycleEvent>>(
                `${endpoints.lifecycleEvents.list()}${buildQueryString(params)}`),
    }
}

function createRealOnboardingService(): ST.OnboardingService {
    return {
        submit: (req: OnboardingRequest) =>
            api.post<Onboarding>(endpoints.onboarding.submit(), req),
        checkPartyName: (displayName: string) =>
            api.get(endpoints.onboarding.checkPartyName(displayName)),
        checkIdentifier: (type: string, value: string) =>
            api.get(endpoints.onboarding.checkIdentifier(type, value)),
    }
}

function createRealAccountsService(): ST.AccountsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<import('@/types/account').Account>>(`${endpoints.accounts.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<import('@/types/account').Account>(endpoints.accounts.detail(id)),
        createAccount: (identityId: string, targetSystemId: string) =>
            api.post<import('@/types/account').AccountLifecycle>(endpoints.accounts.createAccount(identityId, targetSystemId), {}),
        disableOnSystem: (accountId: string) =>
            api.post<import('@/types/account').AccountLifecycle>(endpoints.accounts.disableOnSystem(accountId), {}),
        enableOnSystem: (accountId: string) =>
            api.post<import('@/types/account').AccountLifecycle>(endpoints.accounts.enableOnSystem(accountId), {}),
        deleteOnSystem: (accountId: string) =>
            api.post<import('@/types/account').AccountLifecycle>(endpoints.accounts.deleteOnSystem(accountId), {}),
        disable: (accountId: string, request: ST.AccountActionRequest) =>
            api.post<import('@/types/account').Account>(endpoints.accounts.disable(accountId), request),
        enable: (accountId: string, request: ST.AccountActionRequest) =>
            api.post<import('@/types/account').Account>(endpoints.accounts.enable(accountId), request),
        lock: (accountId: string, request: ST.AccountActionRequest) =>
            api.post<import('@/types/account').Account>(endpoints.accounts.lock(accountId), request),
        delete: (accountId: string, request: ST.AccountActionRequest) =>
            api.post<import('@/types/account').Account>(endpoints.accounts.delete(accountId), request),
        statusEvents: (accountId: string, params?: QueryParams) =>
            api.get<Page<import('@/types/account').AccountStatusEvent>>(
                `${endpoints.accounts.statusEvents(accountId)}${buildQueryString(params)}`),
    }
}

function createRealFunctionGroupsService(): ST.FunctionGroupsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<FunctionGroup>>(`${endpoints.functionGroups.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<FunctionGroup>(endpoints.functionGroups.detail(id)),
        create: (req: import('@/types/function-group').FunctionGroupCreateRequest) =>
            api.post<FunctionGroup>(endpoints.functionGroups.list(), req),
        attributes: (id: string) =>
            api.get<unknown[]>(endpoints.functionGroups.attributes(id)),
        accessProfiles: (id: string) =>
            api.get<FunctionGroupAccessProfileList>(endpoints.functionGroups.accessProfiles(id)),
        owners: (id: string) =>
            api.get<import('@/types/owner').OwnerResponse[]>(endpoints.functionGroups.owners(id)),
        addOwner: (id: string, req: import('@/types/owner').OwnerRequest) =>
            api.post<import('@/types/owner').OwnerResponse>(endpoints.functionGroups.owners(id), req),
        removeOwner: (id: string, ownerId: string) =>
            api.delete<void>(endpoints.functionGroups.removeOwner(id, ownerId)),
    }
}

function createRealFunctionGroupAssignmentsService(): ST.FunctionGroupAssignmentsService {
    return {
        byIdentity: (identityId: string) =>
            api.get<FunctionGroupAssignment[]>(endpoints.functionGroupAssignments.byIdentity(identityId)),
        revoke: (identityId: string, assignmentId: string) =>
            api.post(endpoints.functionGroupAssignments.revoke(identityId, assignmentId), {}),
        restore: (identityId: string, assignmentId: string) =>
            api.post(endpoints.functionGroupAssignments.restore(identityId, assignmentId), {}),
        extend: (identityId: string, assignmentId: string, req: unknown) =>
            api.post(endpoints.functionGroupAssignments.extend(identityId, assignmentId), req),
    }
}

function createRealFunctionGroupRequestsService(): ST.FunctionGroupRequestsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<FunctionGroupRequest>>(`${endpoints.functionGroupRequests.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<FunctionGroupRequest>(endpoints.functionGroupRequests.detail(id)),
        create: (req: unknown) =>
            api.post<FunctionGroupRequest>(endpoints.functionGroupRequests.create(), req),
        cancel: (id: string, req?: unknown) =>
            api.post(endpoints.functionGroupRequests.cancel(id), req ?? {}),
        approveItem: (id: string, itemId: string, req: unknown) =>
            api.post<FunctionGroupRequestItem>(endpoints.functionGroupRequests.approveItem(id, itemId), req),
        rejectItem: (id: string, itemId: string, req: unknown) =>
            api.post<FunctionGroupRequestItem>(endpoints.functionGroupRequests.rejectItem(id, itemId), req),
        cancelItem: (id: string, itemId: string, req?: unknown) =>
            api.post(endpoints.functionGroupRequests.cancelItem(id, itemId), req ?? {}),
        batchApprove: (req: unknown) =>
            api.post(endpoints.functionGroupRequests.batchApprove(), req),
        batchReject: (req: unknown) =>
            api.post(endpoints.functionGroupRequests.batchReject(), req),
        myRequests: (params?: QueryParams) =>
            api.get<Page<FunctionGroupRequest>>(`${endpoints.functionGroupRequests.myRequests()}${buildQueryString(params)}`),
        myRequestDetail: (id: string) =>
            api.get<FunctionGroupRequest>(endpoints.functionGroupRequests.myRequestDetail(id)),
        myApprovals: (params?: QueryParams) =>
            api.get<Page<FunctionGroupRequestItem>>(`${endpoints.functionGroupRequests.myApprovals()}${buildQueryString(params)}`),
        myApprovalsCount: () =>
            api.get<number>(endpoints.functionGroupRequests.myApprovalsCount()),
        holdings: (beneficiaryIdentityId: string, functionGroupIds: string[]) =>
            api.get<Record<string, import('@/types/holding-status').HoldingStatusResponse>>(
                `${endpoints.functionGroupRequests.holdings()}?beneficiaryIdentityId=${beneficiaryIdentityId}&functionGroupIds=${functionGroupIds.join(',')}`),
    }
}

function createRealProvisioningService(): ST.ProvisioningService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<import('@/types/provisioning').ProvisioningTask>>(`${endpoints.provisioningTasks.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<import('@/types/provisioning').ProvisioningTask>(endpoints.provisioningTasks.detail(id)),
        stats: () =>
            api.get<{queued: number; running: number; failed: number; done: number; total: number}>(endpoints.provisioningTasks.stats()),
        history: (params?: QueryParams) =>
            api.get<Page<import('@/types/provisioning').ProvisioningTaskHistory>>(`${endpoints.provisioningTasks.history()}${buildQueryString(params)}`),
        processAll: () =>
            api.post(endpoints.provisioningTasks.processAll(), {}),
        requeueReady: () =>
            api.post(endpoints.provisioningTasks.requeueReady(), {}),
        archive: () =>
            api.post(endpoints.provisioningTasks.archive(), {}),
        retry: (id: string) =>
            api.post<import('@/types/provisioning').ProvisioningTask>(endpoints.provisioningTasks.retry(id), {}),
        retryBulk: (taskIds: string[]) =>
            api.post<import('@/types/provisioning').BulkRetry>(endpoints.provisioningTasks.retryFailed(), {taskIds}),
    }
}

function createRealPartyRelationshipsService(): ST.PartyRelationshipsService {
    return {
        fromParty: (partyId: string) =>
            api.get<import('@/types/party').PartyRelationship[]>(endpoints.partyRelationships.fromParty(partyId)),
        toParty: (partyId: string) =>
            api.get<import('@/types/party').PartyRelationship[]>(endpoints.partyRelationships.toParty(partyId)),
        create: (req: import('@/types/party').PartyRelationshipCreateRequest) =>
            api.post<import('@/types/party').PartyRelationship>(endpoints.partyRelationships.create(), req),
        end: (id: string) =>
            api.put<import('@/types/party').PartyRelationship>(endpoints.partyRelationships.end(id), {}),
    }
}

function createRealIdentityRelationshipsService(): ST.IdentityRelationshipsService {
    return {
        fromIdentity: (identityId: string, type?: string) =>
            api.get<import('@/types/identity').IdentityRelationship[]>(
                endpoints.identityRelationships.fromIdentity(identityId, type)),
        toIdentity: (identityId: string, type?: string) =>
            api.get<import('@/types/identity').IdentityRelationship[]>(
                endpoints.identityRelationships.toIdentity(identityId, type)),
        create: (req) =>
            api.post<import('@/types/identity').IdentityRelationship>(
                endpoints.identityRelationships.create(), req),
        end: (id: string) =>
            api.put<import('@/types/identity').IdentityRelationship>(
                endpoints.identityRelationships.end(id), {}),
    }
}

function createRealPartyRelationshipSchemaService(): ST.PartyRelationshipSchemaService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<import('@/types/party').PartyRelationshipSchemaEntry>>(`${endpoints.partyRelationshipSchema.list()}${buildQueryString(params)}`),
        create: (req: unknown) =>
            api.post<import('@/types/party').PartyRelationshipSchemaEntry>(endpoints.partyRelationshipSchema.create(), req),
        update: (id: string, req: unknown) =>
            api.put<import('@/types/party').PartyRelationshipSchemaEntry>(endpoints.partyRelationshipSchema.update(id), req),
        deactivate: (id: string) =>
            api.delete(endpoints.partyRelationshipSchema.deactivate(id)),
        violations: (params?: QueryParams) =>
            api.get<Page<import('@/types/party').PartyRelationshipSchemaViolation>>(`${endpoints.partyRelationshipSchema.violations()}${buildQueryString(params)}`),
        enforcementMode: () =>
            api.get(endpoints.partyRelationshipSchema.enforcementMode()),
        updateEnforcementMode: (req: unknown) =>
            api.put(endpoints.partyRelationshipSchema.enforcementMode(), req),
    }
}

function createRealCachesAdminService(): ST.CachesAdminService {
    return {
        list: () =>
            api.get<import('@/types/cache-admin').CacheStatusResponse[]>(endpoints.caches.list()),
        evict: (name: string) =>
            api.post<void>(endpoints.caches.evict(name), {}),
        reload: (name: string) =>
            api.post<void>(endpoints.caches.reload(name), {}),
    }
}

function createRealLlmPromptsService(): ST.LlmPromptsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<import('@/types/llm-prompts').LlmPromptTemplate>>(`${endpoints.llmPrompts.list()}${buildQueryString(params)}`),
        detail: (key: string) =>
            api.get<import('@/types/llm-prompts').LlmPromptTemplate>(endpoints.llmPrompts.detail(key)),
        update: (key: string, req: unknown) =>
            api.put<import('@/types/llm-prompts').LlmPromptTemplate>(endpoints.llmPrompts.update(key), req),
        restoreDefault: (key: string) =>
            api.post<import('@/types/llm-prompts').LlmPromptTemplate>(endpoints.llmPrompts.restoreDefault(key), {}),
        renderPreview: (key: string, req: unknown) =>
            api.post<import('@/types/llm-prompts').LlmPromptRenderPreview>(endpoints.llmPrompts.renderPreview(key), req),
        test: (key: string, req: unknown) =>
            api.post<import('@/types/llm-prompts').LlmPromptTest>(endpoints.llmPrompts.test(key), req),
    }
}

function createRealNotificationTemplatesService(): ST.NotificationTemplatesService {
    type ListItem = import('@/types/notification-templates').NotificationTemplateListItem
    type Detail = import('@/types/notification-templates').NotificationTemplateDetail
    type TestSend = import('@/types/notification-templates').NotificationTemplateTestSend
    return {
        list: (params?: QueryParams) =>
            api.get<Page<ListItem>>(`${endpoints.notificationTemplates.list()}${buildQueryString(params)}`),
        detail: (key: string) =>
            api.get<Detail>(endpoints.notificationTemplates.detail(key)),
        update: (key: string, req: unknown) =>
            api.put<Detail>(endpoints.notificationTemplates.update(key), req),
        restoreDefault: (key: string) =>
            api.post<Detail>(endpoints.notificationTemplates.restoreDefault(key), {}),
        testSend: (key: string) =>
            api.post<TestSend>(endpoints.notificationTemplates.testSend(key), {}),
    }
}

function createRealMyAccessService(): ST.MyAccessService {
    return {
        assignments: (params?: QueryParams | Record<string, string>) =>
            api.get(`${endpoints.myAccess.assignments()}${buildQueryString(params)}`),
        timeline: (params?: QueryParams) =>
            api.get(`${endpoints.myAccess.timeline()}${buildQueryString(params)}`),
        functionGroupAssignments: (params?: QueryParams | Record<string, string>) =>
            api.get(`${endpoints.myAccess.functionGroupAssignments()}${buildQueryString(params)}`),
        functionGroupTimeline: (params?: QueryParams) =>
            api.get(`${endpoints.myAccess.functionGroupTimeline()}${buildQueryString(params)}`),
        effectiveAccess: (params?: QueryParams | Record<string, string>) =>
            api.get(`${endpoints.myAccess.effectiveAccess()}${buildQueryString(params)}`),
    }
}

function createRealNodesService(): ST.NodesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<ClusterNode>>(`${endpoints.nodes.list()}${buildQueryString(params)}`),
        current: () =>
            api.get<ClusterNode>(endpoints.nodes.current()),
        remove: (nodeId: string) =>
            api.delete(endpoints.nodes.remove(nodeId)),
    }
}

function createRealUsersService(): ST.UsersService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<UserWithRoles>>(`${endpoints.users.list()}${buildQueryString(params)}`),
    }
}

function createRealLinksService(path: () => string): ST.EntityLinksService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<EntityLink>>(`${path()}${buildQueryString(params)}`),
    }
}

function createRealAdminService(): ST.AdminService {
    return {
        generateTestData: (req?: unknown) =>
            api.post(endpoints.admin.generateTestData(), req ?? {}),
        frontendError: (req: unknown) =>
            api.post(endpoints.admin.frontendError(), req),
    }
}

function createRealClockService(): ST.ClockService {
    return {
        status: () =>
            api.get(endpoints.clock.status()),
        setTime: (time: string) =>
            api.post(endpoints.clock.status(), {time}),
        reset: () =>
            api.delete(endpoints.clock.status()),
    }
}

function createRealUtilitiesService(): ST.UtilitiesService {
    return {
        inferPattern: (req: unknown) =>
            api.post(endpoints.utilities.inferPattern(), req),
    }
}

function createRealExecutionLogsService(): ST.ExecutionLogsService {
    return {
        byReference: (referenceId: string, params?: QueryParams) =>
            api.get<Page<unknown>>(`${endpoints.executionLogs.byReference(referenceId)}${buildQueryString(params)}`),
    }
}

function createRealRuleExpressionsService(): ST.RuleExpressionsService {
    return {
        suggestions: (req: unknown) =>
            api.post<unknown>(endpoints.ruleExpressions.suggestions(), req),
    }
}

function createRealAuthService(): ST.AuthService {
    return {
        currentUser: () =>
            api.get(endpoints.users.list()),
    }
}

function createRealConfigService(): ST.ConfigService {
    return {
        get: () =>
            api.get(endpoints.config()),
    }
}

function createRealCertificationCampaignsService(): ST.CertificationCampaignsService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<CertificationCampaign>>(`${endpoints.certificationCampaigns.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<CertificationCampaign>(endpoints.certificationCampaigns.detail(id)),
        create: (req: CreateCampaignRequest) =>
            api.post<CertificationCampaign>(endpoints.certificationCampaigns.create(), req),
        remove: (id: string) =>
            api.delete(endpoints.certificationCampaigns.remove(id)),
        owners: (id: string) =>
            api.get<import('@/types/owner').OwnerResponse[]>(endpoints.certificationCampaigns.owners(id)),
        addOwner: (id: string, req: import('@/types/owner').OwnerRequest) =>
            api.post<import('@/types/owner').OwnerResponse>(endpoints.certificationCampaigns.owners(id), req),
        removeOwner: (id: string, ownerId: string) =>
            api.delete<void>(endpoints.certificationCampaigns.removeOwner(id, ownerId)),
    }
}

function createRealCertificationPoliciesService(): ST.CertificationPoliciesService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<CertificationPolicy>>(`${endpoints.certificationPolicies.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<CertificationPolicy>(endpoints.certificationPolicies.detail(id)),
        create: (req: CreateCertificationPolicyRequest) =>
            api.post<CertificationPolicy>(endpoints.certificationPolicies.create(), req),
        update: (id: string, req: UpdateCertificationPolicyRequest) =>
            api.put<CertificationPolicy>(endpoints.certificationPolicies.update(id), req),
        remove: (id: string) =>
            api.delete(endpoints.certificationPolicies.remove(id)),
        campaignSummary: (id: string) =>
            api.get<CampaignSummaryByPolicy>(endpoints.certificationPolicies.campaignSummary(id)),
        testExpression: (req: TestExpressionRequest) =>
            api.post<TestExpression>(endpoints.certificationPolicies.testExpression(), req),
        previewScope: (req: PreviewScopeRequest) =>
            api.post<PreviewScopeResponse>(endpoints.certificationPolicies.previewScope(), req),
        owners: (id: string) =>
            api.get<import('@/types/owner').OwnerResponse[]>(endpoints.certificationPolicies.owners(id)),
        addOwner: (id: string, req: import('@/types/owner').OwnerRequest) =>
            api.post<import('@/types/owner').OwnerResponse>(endpoints.certificationPolicies.owners(id), req),
        removeOwner: (id: string, ownerId: string) =>
            api.delete<void>(endpoints.certificationPolicies.removeOwner(id, ownerId)),
    }
}

function createRealCertificationReviewsService(): ST.CertificationReviewsService {
    return {
        detail: (id: string) =>
            api.get<CertificationReviewItem>(endpoints.certificationReviews.detail(id)),
    }
}

function createRealExecutionsService(): ST.ExecutionsService {
    return {
        detail: (id: string) =>
            api.get<JobExecution>(endpoints.executions.detail(id)),
    }
}

function createRealRehireReviewService(): ST.RehireReviewService {
    return {
        list: (params?: QueryParams) =>
            api.get<Page<RehireReviewCandidate>>(`${endpoints.rehireReview.list()}${buildQueryString(params)}`),
        detail: (id: string) =>
            api.get<RehireReviewCandidate>(endpoints.rehireReview.detail(id)),
        decide: (id: string, req: RehireReviewDecideRequest) =>
            api.post<RehireReviewDecideResponse>(endpoints.rehireReview.decide(id), req),
        count: (status?: RehireReviewStatus) =>
            api.get<RehireReviewCountResponse>(`${endpoints.rehireReview.count()}${status ? `?status=${encodeURIComponent(status)}` : ''}`),
        listMine: (params?: QueryParams) =>
            api.get<Page<RehireReviewCandidate>>(`${endpoints.rehireReview.mine.list()}${buildQueryString(params)}`),
        countMine: (status?: RehireReviewStatus) =>
            api.get<RehireReviewCountResponse>(`${endpoints.rehireReview.mine.count()}${status ? `?status=${encodeURIComponent(status)}` : ''}`),
        previousAccess: (candidateId: string) =>
            api.get<PreviousAccessProfile[]>(endpoints.rehireReview.mine.previousAccess(candidateId)),
    }
}

function createRealProvisioningTasksService(): ST.ProvisioningTasksService {
    const full = createRealProvisioningService()
    return {
        list: full.list,
        detail: full.detail,
        stats: () =>
            api.get<{queued: number; running: number; failed: number; completed: number}>(endpoints.provisioningTasks.stats()),
        retry: full.retry,
        retryBulk: full.retryBulk,
    }
}

export function createRealServices(): Services {
    return {
        parties: createRealPartiesService(),
        identities: createRealIdentitiesService(),
        roles: createRealRolesService(),
        permissions: createRealPermissionsService(),
        systems: createRealSystemsService(),
        resources: createRealResourcesService(),
        enforcementObjects: createRealEnforcementObjectsService(),
        accessProfiles: createRealAccessProfilesService(),
        approvalPolicies: createRealApprovalPoliciesService(),
        serviceAccounts: createRealServiceAccountsService(),
        apiKeys: createRealApiKeysService(),
        serviceAccountRoles: createRealServiceAccountRolesService(),
        restrictionRules: createRealRestrictionRulesService(),
        restrictionRuleRequests: createRealRestrictionRuleRequestsService(),
        accessRequests: createRealAccessRequestsService(),
        certification: createRealCertificationService(),
        certificationCampaigns: createRealCertificationCampaignsService(),
        certificationPolicies: createRealCertificationPoliciesService(),
        certificationReviews: createRealCertificationReviewsService(),
        attributeDefinitions: createRealAttributeDefinitionsService(),
        valueLists: createRealValueListsService(),
        types: createRealTypesService(),
        notifications: createRealNotificationsService(),
        jobs: createRealJobsService(),
        executions: createRealExecutionsService(),
        errorLogs: createRealErrorLogsService(),
        observability: createRealObservabilityService(),
        inboundConnectors: createRealInboundConnectorsService(),
        outboundConnectors: createRealOutboundConnectorsService(),
        reporting: createRealReportingService(),
        scripts: createRealScriptsService(),
        formLayouts: createRealFormLayoutsService(),
        autoAssignRules: createRealAutoAssignRulesService(),
        constraints: createRealConstraintsService(),
        violations: createRealViolationsService(),
        delegations: createRealDelegationsService(),
        analytics: createRealAnalyticsService(),
        onboarding: createRealOnboardingService(),
        accounts: createRealAccountsService(),
        functionGroups: createRealFunctionGroupsService(),
        functionGroupAssignments: createRealFunctionGroupAssignmentsService(),
        functionGroupRequests: createRealFunctionGroupRequestsService(),
        provisioning: createRealProvisioningService(),
        provisioningTasks: createRealProvisioningTasksService(),
        partyRelationships: createRealPartyRelationshipsService(),
        partyRelationshipSchema: createRealPartyRelationshipSchemaService(),
        cachesAdmin: createRealCachesAdminService(),
        identityRelationships: createRealIdentityRelationshipsService(),
        llmPrompts: createRealLlmPromptsService(),
        notificationTemplates: createRealNotificationTemplatesService(),
        myAccess: createRealMyAccessService(),
        nodes: createRealNodesService(),
        users: createRealUsersService(),
        usersLinks: createRealLinksService(endpoints.users.links),
        identitiesLinks: createRealLinksService(endpoints.identitiesLinks),
        systemsLinks: createRealLinksService(endpoints.systemsLinks),
        resourcesLinks: createRealLinksService(endpoints.resourcesLinks),
        enforcementObjectsLinks: createRealLinksService(endpoints.enforcementObjectsLinks),
        accessProfilesLinks: createRealLinksService(endpoints.accessProfilesLinks),
        reportCategoriesLinks: createRealLinksService(endpoints.reportCategoriesLinks),
        entityLinks: createRealLinksService(endpoints.links),
        admin: createRealAdminService(),
        clock: createRealClockService(),
        utilities: createRealUtilitiesService(),
        executionLogs: createRealExecutionLogsService(),
        ruleExpressions: createRealRuleExpressionsService(),
        auth: createRealAuthService(),
        config: createRealConfigService(),
        entityAttributes: createRealEntityAttributesService(),
        rehireReview: createRealRehireReviewService(),
    }
}

function createRealEntityAttributesService(): ST.EntityAttributesService {
    return {
        list: (entityType: AttributeEntityType, entityId: string) =>
            api.get<unknown[]>(endpoints.entityAttributes.list(entityType, entityId)),
        create: (entityType: AttributeEntityType, entityId: string, req: unknown) =>
            api.post(endpoints.entityAttributes.create(entityType, entityId), req),
        update: (entityType: AttributeEntityType, entityId: string, req: unknown) =>
            api.put(endpoints.entityAttributes.update(entityType, entityId), req),
        remove: (entityType: AttributeEntityType, entityId: string, attributeId: string) =>
            api.delete(endpoints.entityAttributes.remove(entityType, entityId, attributeId)),
    }
}
